/* eslint-disable no-underscore-dangle */
import { AllHTMLAttributes, useRef } from 'react';
import { MessageDescriptor, useIntl, defineMessages } from 'react-intl';
import { Link } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLongArrowRight } from '@fortawesome/free-solid-svg-icons';
import Loader from '../Loader/Loader';
import Tooltip from '../Tooltip/Tooltip';
import SvgIcon from '../SvgIcon/SvgIcon';
import copyIcon from '../CopyButton/Copy_white.svg';
import styles from './Button.module.scss';

const messages = defineMessages({
	copied: {
		id: 'payments.copied',
		defaultMessage: 'Copied',
	},
});

// eslint-disable-next-line no-shadow
export enum ButtonStyle {
	BORDERLESS = 'borderless',
	LINK = 'link',
	PRIMARY = 'primary',
	SECONDARY = 'secondary',
	SECONDARY_WITHOUT_BORDER = 'secondaryWithoutBorder',
	TERTIARY = 'tertiary',
}

// eslint-disable-next-line no-shadow
export enum ButtonType {
	ANCHOR_LINK = 'a',
	BUTTON = 'button',
	COPY = 'copy',
	LINK = 'link',
	SUBMIT = 'submit',
}

interface ButtonProps extends AllHTMLAttributes<HTMLElement> {
	buttonStyle?: ButtonStyle;
	hasIcon?: boolean;
	hasTransition?: boolean;
	link?: string | MessageDescriptor;
	text?: string | MessageDescriptor;
	type?: ButtonType;
	children?: React.ReactNode;
	isDisabled?: boolean;
	isLoading?: boolean;
	plusIcon?: boolean;
	isFullWidth?: boolean;
	minWidth?: number;
	dataToCopy?: string;
}

const Button = ({
	buttonStyle = ButtonStyle.PRIMARY,
	className,
	hasIcon = false,
	hasTransition = false,
	link,
	text,
	type,
	children,
	isDisabled = false,
	isLoading = false,
	plusIcon = false,
	isFullWidth = false,
	minWidth,
	dataToCopy,
	...attributes
}: ButtonProps) => {
	const { formatMessage } = useIntl();
	const tooltipRef = useRef<Element & { _tippy: any }>(null);
	const buttonClassName = classNames(
		styles.button,
		styles[buttonStyle],
		hasTransition && styles.hasTransition,
		isDisabled && styles.disabled,
		className
	);
	const buttonText = text && typeof text !== 'string' ? formatMessage(text) : text;
	const buttonLink = link;

	const wrappedAttributes = {
		// this is for testing purposes. since anchor does not carry a disabled indicator assign a custom attribute for asserts
		...(isDisabled ? { 'data-disabled': true } : {}),
		...attributes,
	};

	const child = children || (
		<>
			{plusIcon && <span className={styles.plus}>+</span>}
			{type === ButtonType.COPY && <SvgIcon className={styles.copyIcon} src={copyIcon} />}
			{buttonText}
			{hasIcon && (
				<FontAwesomeIcon className={styles.icon} icon={faLongArrowRight} size="1x" />
			)}
		</>
	);

	const onCopy = () => {
		tooltipRef.current?._tippy?.show();
		setTimeout(() => tooltipRef.current?._tippy?.hide(), 1000);
	};

	switch (type) {
		case ButtonType.BUTTON:
		case ButtonType.SUBMIT:
			return (
				<button
					className={buttonClassName}
					// eslint-disable-next-line react/button-has-type
					type={type}
					{...wrappedAttributes}
					disabled={isDisabled || isLoading}
					style={{ minWidth: `${minWidth}px`, ...(isFullWidth ? { width: '100%' } : {}) }}
				>
					{isLoading ? <Loader className={styles.loader} /> : child}
				</button>
			);

		case ButtonType.COPY:
			return (
				<CopyToClipboard text={dataToCopy || ''} onCopy={() => onCopy()}>
					<div className={styles.flex}>
						<Tooltip
							ref={tooltipRef}
							content={formatMessage(messages.copied)}
							trigger="manual"
						>
							<div className={styles.flex}>
								<button
									className={buttonClassName}
									// eslint-disable-next-line react/button-has-type
									type="button"
									{...wrappedAttributes}
									disabled={isDisabled || isLoading}
									style={{
										minWidth: `${minWidth}px`,
										...(isFullWidth ? { width: '100%' } : {}),
									}}
								>
									{isLoading ? <Loader className={styles.loader} /> : child}
								</button>
							</div>
						</Tooltip>
					</div>
				</CopyToClipboard>
			);

		case ButtonType.LINK:
			return (
				<Link
					className={buttonClassName}
					to={buttonLink as string}
					{...wrappedAttributes}
					style={{ minWidth: `${minWidth}px` }}
				>
					<div>{isLoading ? <Loader className={styles.loader} /> : child}</div>
				</Link>
			);

		case ButtonType.ANCHOR_LINK:
		default:
			return (
				<a
					href={buttonLink as string}
					className={buttonClassName}
					{...wrappedAttributes}
					style={{ minWidth: `${minWidth}px` }}
				>
					<div>{isLoading ? <Loader className={styles.loader} /> : child}</div>
				</a>
			);
	}
};

export default Button;
